import { BACKBLAZE_PRICE_CHECKED, BACKBLAZE_PRICE_PER_TB_MONTH } from "@bigbear/shared";

/**
 * Provider pricing, for the worked example on the Providers page. Numbers are
 * each provider's own published list price, not a negotiated or promotional
 * rate, and they move — a business whose margin depends on a fraction of a
 * cent per gigabyte updates these often. Treat every figure here as a rough
 * guide to the shape of the bill, not a quote, and check the provider's own
 * pricing page before deciding.
 *
 * Backblaze's own figure reads the shared `@bigbear/shared` constant, the same
 * one the desktop wizard's cost estimate uses, so the two cannot drift apart.
 * Its row carries its own `checked` date from that constant — bumping the
 * shared price must not re-date the eight prices nobody re-checked.
 */

/** When the prices below were last checked, for every row that does not carry
 * a `checked` date of its own. */
export const PRICES_CHECKED = "September 2026";

export interface ProviderPrice {
  id: string;
  provider: string;
  className: "sync" | "object-store" | "hyperscaler";
  /** One line: the plan or tier this price is for. */
  plan: string;
  /** Dollars per TB per month, storage only, rounded to the dollar except
   * where a provider prices in cents and rounding would misstate it —
   * Backblaze at $6.95 and GCS Archive at $1.20 today. Backblaze's reads the
   * shared constant, so it matches the desktop wizard's estimate exactly. */
  storagePerTbMonth: number;
  /** What downloading data back out costs, in words — this is the term that
   * actually varies between the three classes, so it is prose rather than a
   * single number. */
  egress: string;
  /** Anything that changes the shape of the bill: minimums, per-account
   * caps, retrieval delay. */
  notes: string;
  source: string;
  /** When this row's price was last checked, when it is checked on its own
   * schedule rather than with the table. Only Backblaze has one, from the
   * shared constant. */
  checked?: string;
}

/**
 * One row per plan actually worth comparing. Sync providers are priced per
 * account at a fixed tier rather than per TB, so `storagePerTbMonth` for
 * those rows is that tier's price divided by its capacity, to make the
 * per-TB comparison possible at all — the plan column carries the real
 * quote.
 */
export const PROVIDER_PRICES: ProviderPrice[] = [
  {
    id: "google-one-2tb",
    provider: "Google One",
    className: "sync",
    plan: "2 TB, $9.99/mo (or $99.99/yr)",
    storagePerTbMonth: 5,
    egress: "Free — downloads count against nothing",
    notes: "750 GB/day upload cap per account regardless of thread count",
    source: "one.google.com/about/plans",
  },
  {
    id: "dropbox-plus-2tb",
    provider: "Dropbox",
    className: "sync",
    plan: "Plus, 2 TB, $11.99/mo (or ~$9.99/mo billed yearly)",
    storagePerTbMonth: 6,
    egress: "Free",
    notes: "Writes to one folder serialise on Dropbox's side; reads do not",
    source: "dropbox.com/plans",
  },
  {
    id: "onedrive-1tb",
    provider: "Microsoft 365",
    className: "sync",
    plan: "Personal, 1 TB, $6.99/mo (or $69.99/yr)",
    storagePerTbMonth: 7,
    egress: "Free",
    notes: "Graph API throttles per app and tenant; guidance is to run few streams",
    source: "microsoft.com/microsoft-365/buy",
  },
  {
    id: "backblaze-b2",
    provider: "Backblaze B2",
    className: "object-store",
    plan: "Pay-as-you-go",
    storagePerTbMonth: BACKBLAZE_PRICE_PER_TB_MONTH,
    egress: "Free up to 3× what you store each month; $0.01/GB beyond that",
    notes: "No minimum retention or account size; a natural default for a large vault",
    source: "backblaze.com/cloud-storage/pricing",
    checked: BACKBLAZE_PRICE_CHECKED,
  },
  {
    id: "wasabi",
    provider: "Wasabi",
    className: "object-store",
    plan: "Pay-as-you-go",
    storagePerTbMonth: 7,
    egress: "Free (subject to a reasonable-use policy)",
    notes: "1 TB minimum billed capacity and a 90-day minimum retention per object",
    source: "wasabi.com/cloud-storage-pricing",
  },
  {
    id: "s3-standard",
    provider: "AWS S3",
    className: "hyperscaler",
    plan: "S3 Standard",
    storagePerTbMonth: 23,
    egress: "First 100 GB/mo free, then about $90/TB",
    notes: "Ingress is always free; egress is the cost that matters here",
    source: "aws.amazon.com/s3/pricing",
  },
  {
    id: "s3-deep-archive",
    provider: "AWS S3",
    className: "hyperscaler",
    plan: "Glacier Deep Archive",
    storagePerTbMonth: 1,
    egress: "About $90/TB, plus a per-GB retrieval fee, plus up to 12 hours to thaw",
    notes: "180-day minimum storage duration; an early delete is billed the remainder",
    source: "aws.amazon.com/s3/pricing",
  },
  {
    id: "gcs-archive",
    provider: "Google Cloud Storage",
    className: "hyperscaler",
    plan: "Archive class",
    storagePerTbMonth: 1.2,
    egress: "About $120/TB, plus a per-GB retrieval fee",
    notes: "365-day minimum storage duration",
    source: "cloud.google.com/storage/pricing",
  },
  {
    id: "azure-archive",
    provider: "Azure Blob",
    className: "hyperscaler",
    plan: "Archive tier",
    storagePerTbMonth: 2,
    egress: "About $87/TB, plus a per-GB retrieval fee, plus hours to rehydrate",
    notes: "180-day minimum storage duration",
    source: "azure.microsoft.com/pricing/details/storage/blobs",
  },
];

export interface WorkedExample {
  id: string;
  label: string;
  /** What a year of storing 1 TB, then restoring all of it once, costs — as a
   * rounded whole-dollar range, since retrieval and egress pricing has steps
   * this simple model does not capture exactly. */
  costLabel: string;
}

/** A year of storing the example's 1 TB, to the whole dollar. */
export function yearOfOneTb(pricePerTbMonth: number): number {
  return Math.round(pricePerTbMonth * 12);
}

/** The same, for a row of the table above. Every storage figure below goes
 * through here rather than being written down, so editing a price — or the
 * shared Backblaze constant behind one — moves the worked example with it. */
function storageYear(priceId: string): number {
  const price = PROVIDER_PRICES.find((p) => p.id === priceId);
  if (!price) throw new Error(`No provider price with id "${priceId}".`);
  return yearOfOneTb(price.storagePerTbMonth);
}

/** What pulling the 1 TB back out costs, in whole dollars, matching the
 * `egress` prose on the rows above. These stay written down: egress is priced
 * in steps, with per-GB retrieval fees on the archive tiers that this simple
 * model does not carry. Deep Archive's is a range, so its total takes the
 * midpoint. */
const S3_STANDARD_RESTORE = 90;
const DEEP_ARCHIVE_RESTORE_LOW = 90;
const DEEP_ARCHIVE_RESTORE_HIGH = 100;

const s3Standard = storageYear("s3-standard");
const deepArchive = storageYear("s3-deep-archive");
const deepArchiveRestore = (DEEP_ARCHIVE_RESTORE_LOW + DEEP_ARCHIVE_RESTORE_HIGH) / 2;

/** The 2 TB sync plans, both prorated to the 1 TB this example stores, so the
 * two rows compare on the same basis. Neither quotes the whole plan; the plan
 * column above carries the real subscription price. */
const googleOne = storageYear("google-one-2tb");
const dropbox = storageYear("dropbox-plus-2tb");

/**
 * "Store 1 TB for a year, then restore it once" — the comparison that
 * actually separates the three classes, because storage price alone makes
 * the hyperscalers look cheap and hides where the bill really comes from.
 */
export const WORKED_EXAMPLES: WorkedExample[] = [
  {
    id: "google-one",
    label: "Google One (2 TB plan, prorated)",
    costLabel: `~$${googleOne} (the subscription; the restore is free)`,
  },
  {
    id: "dropbox",
    label: "Dropbox Plus (2 TB plan, prorated)",
    costLabel: `~$${dropbox} (the subscription; the restore is free)`,
  },
  {
    id: "b2",
    label: "Backblaze B2",
    costLabel: `~$${storageYear("backblaze-b2")} storage, restore free (under the 3× allowance)`,
  },
  {
    id: "wasabi",
    label: "Wasabi",
    costLabel: `~$${storageYear("wasabi")} storage, restore free`,
  },
  {
    id: "s3-standard",
    label: "AWS S3 Standard",
    costLabel: `~$${s3Standard} storage + ~$${S3_STANDARD_RESTORE} to restore ≈ $${s3Standard + S3_STANDARD_RESTORE}`,
  },
  {
    id: "s3-deep-archive",
    label: "AWS S3 Glacier Deep Archive",
    costLabel: `~$${deepArchive} storage + ~$${DEEP_ARCHIVE_RESTORE_LOW}–$${DEEP_ARCHIVE_RESTORE_HIGH} to restore ≈ $${deepArchive + deepArchiveRestore}`,
  },
];
